/**
 * React Error Boundary component for graceful error handling.
 *
 * @remarks
 * This component implements the Error Boundary pattern required by
 * Engineering Principles Section II (TypeScript). It catches React
 * rendering errors and displays a user-friendly fallback UI instead
 * of crashing the entire application.
 *
 * Error boundaries catch errors during:
 * - Rendering
 * - Lifecycle methods
 * - Constructors of child components
 *
 * Error boundaries do NOT catch errors in:
 * - Event handlers (use try/catch)
 * - Asynchronous code (use try/catch or .catch())
 * - Server-side rendering
 * - Errors thrown in the error boundary itself
 *
 * @example
 * ```tsx
 * // Wrap major routes
 * <ErrorBoundary>
 *   <EnvironmentPage />
 * </ErrorBoundary>
 * ```
 *
 * @public
 */

import { Component, type ReactNode, type ErrorInfo } from 'react'
import { Alert } from '@dc/dads-components'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { emitDevDiagnostic } from '../../lib/devDiagnostics'

/**
 * Props for the ErrorBoundary component.
 *
 * @public
 */
export interface ErrorBoundaryProps {
  /** Child components to render and monitor for errors */
  children: ReactNode
  /**
   * Optional custom fallback UI to display on error.
   *
   * @param error - The error that was caught
   * @param resetError - Function to reset the error state and retry
   */
  fallback?: (error: Error, resetError: () => void) => ReactNode
}

/**
 * State for the ErrorBoundary component.
 *
 * @internal
 */
interface ErrorBoundaryState {
  /** Error that was caught, or null if no error */
  error: Error | null
  /** Additional error information from React */
  errorInfo: ErrorInfo | null
}

/**
 * Error boundary component that catches React rendering errors.
 *
 * @remarks
 * Implements the error boundary pattern required by Engineering Principles.
 * Logs errors with component stack traces and displays user-friendly
 * fallback UI.
 *
 * In development, shows detailed error information. In production, shows
 * generic error message with option to retry.
 *
 * @public
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      error: null,
      errorInfo: null,
    }
  }

  /**
   * React lifecycle method called when an error is caught.
   *
   * @param error - The error that was thrown
   * @param errorInfo - Additional information about the error
   *
   * @remarks
   * This method updates state to trigger the fallback UI render.
   * In development, logs detailed error information to console.
   *
   * TODO: In production, this should log to a structured logging service
   * with correlation IDs (Engineering Principles Section I.4).
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  /**
   * React lifecycle method called after error is caught.
   *
   * @param error - The error that was thrown
   * @param errorInfo - Component stack trace
   *
   * @remarks
   * Logs error details for debugging. In production, this should send
   * errors to a monitoring service with correlation IDs.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })

    if (import.meta.env.DEV) {
      emitDevDiagnostic('error', 'error_boundary_caught_error', {
        errorName: error.name,
        errorMessage: error.message,
        componentStack: errorInfo.componentStack,
      })
    }

    // TODO: Send to structured logging service with correlation ID
    // Engineering Principles Section I.4: structured logging with correlation IDs
  }

  /**
   * Reset the error state and retry rendering.
   *
   * @remarks
   * Called when the user clicks the retry button. Clears error state,
   * allowing the component tree to re-render.
   */
  private resetError = (): void => {
    this.setState({ error: null, errorInfo: null })
  }

  render(): ReactNode {
    const { error, errorInfo } = this.state
    const { children, fallback } = this.props

    if (error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, this.resetError)
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-surface-secondary">
          <div className="max-w-2xl w-full">
            <div className="bg-surface-elevated rounded-xl shadow-lg p-8">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-content mb-2">Something went wrong</h1>
                  <p className="text-content-secondary mb-6">
                    An unexpected error occurred. This has been logged and our team will
                    investigate.
                  </p>

                  {import.meta.env.DEV && (
                    <Alert variant="danger" className="mb-6">
                      <div className="space-y-2">
                        <div className="font-semibold">
                          {error.name}: {error.message}
                        </div>
                        {error.stack && (
                          <pre className="text-xs overflow-x-auto mt-2 p-2 bg-red-50 rounded">
                            {error.stack}
                          </pre>
                        )}
                        {errorInfo?.componentStack && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-sm font-medium">
                              Component Stack
                            </summary>
                            <pre className="text-xs overflow-x-auto mt-2 p-2 bg-red-50 rounded">
                              {errorInfo.componentStack}
                            </pre>
                          </details>
                        )}
                      </div>
                    </Alert>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={this.resetError}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Try Again
                    </button>
                    <button
                      type="button"
                      onClick={() => (window.location.href = '/')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-surface-tertiary text-content-secondary rounded-lg hover:bg-surface-tertiary transition-colors"
                    >
                      Go to Home
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return children
  }
}
